import React, { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { useSearchParams } from "react-router-dom";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";

import {
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    ComposedChart,
    Area,
    Line,
    Bar,
    BarChart,
} from "recharts";

import { StatCard } from "@/components/dashboard/StatCard";
import {
    TrendingUp,
    TrendingDown,
    Users,
    DollarSign,
    CheckCircle2,
    Wallet,
    Banknote,
    AlertTriangle,
} from "lucide-react";

import { usePeople } from "@/context/PeopleContext";
import { useAnalyticsData } from "@/utils/helpers/hooks/useAnalyticsData";
import { formatNumber, getPeriod } from "@/utils/helpers";
import { useAnalyticsUI } from "@/hooks/useAnalyticsUI";

import ProductPreferencesCard from "@/components/Charts/ProductPreferencesCard";

dayjs.extend(weekOfYear);

type RangeType = "all" | "year" | "month" | "week" | "custom";

export const STATUS_COLORS: Record<string, string> = {
    new: "hsl(210 90% 55%)",
    pending: "hsl(45 95% 55%)",
    overdue: "hsl(0 90% 55%)",
    closed: "hsl(142 70% 45%)",
    closedEarly: "hsl(125 80% 40%)",
    returned: "hsl(270 90% 60%)",
    unknown: "hsl(220 10% 50%)",
};

const STATUS_META = [
    { key: "new", label: "Новые" },
    { key: "pending", label: "В процессе" },
    { key: "overdue", label: "Просрочено" },
    { key: "closed", label: "Закрыто" },
    { key: "closedEarly", label: "Досрочно" },
    { key: "returned", label: "Возврат" },
];

function clampPercent(v: number) {
    if (!Number.isFinite(v)) return 0;
    return Math.max(0, Math.min(100, v));
}

function pctBadge(pct: number) {
    const safe = Number.isFinite(pct) ? pct : 0;
    const positive = safe >= 0;

    return (
        <Badge variant="outline" className={positive ? "text-chart-1" : "text-destructive"}>
            {positive ? <TrendingUp className="mr-1 h-3 w-3" /> : <TrendingDown className="mr-1 h-3 w-3" />}
            {positive ? "+" : ""}
            {safe.toFixed(1)}%
        </Badge>
    );
}

function moneyTick(v: any) {
    const n = Number(v || 0);
    if (!Number.isFinite(n)) return "0";
    if (Math.abs(n) >= 1_000_000) return `${Math.round(n / 1_000_000)}M`;
    if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}k`;
    return `${Math.round(n)}`;
}

function RangeFilter({
                         rangeType,
                         startYmd,
                         endYmd,
                         onRangeChange,
                         onDatesChange,
                     }: {
    rangeType: RangeType;
    startYmd: string;
    endYmd: string;
    onRangeChange: (v: RangeType) => void;
    onDatesChange: (start: string, end: string) => void;
}) {
    const isCustom = rangeType === "custom";

    return (
        <Card className="overflow-hidden">
            <CardHeader className="pb-3">
                <div className="flex flex-col gap-1">
                    <CardTitle className="text-base">Период</CardTitle>
                </div>
            </CardHeader>

            <CardContent className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    {(["all", "year", "month", "week", "custom"] as const).map((v) => (
                        <Button
                            key={v}
                            variant={rangeType === v ? "default" : "ghost"}
                            className={`h-9 rounded-full px-4 text-xs sm:text-sm transition-all ${
                                rangeType === v ? "shadow-sm" : "hover:bg-muted/60"
                            }`}
                            onClick={() => onRangeChange(v)}
                        >
                            {v === "all" ? "Все время" : v === "year" ? "Год" : v === "month" ? "Месяц" : v === "week" ? "Неделя" : "Выбор"}
                        </Button>
                    ))}
                </div>

                {isCustom ? (
                    <div className="grid gap-2 sm:grid-cols-2">
                        <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Старт</div>
                            <Input type="date" value={startYmd} onChange={(e) => onDatesChange(e.target.value, endYmd)} />
                        </div>
                        <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Финиш</div>
                            <Input type="date" value={endYmd} onChange={(e) => onDatesChange(startYmd, e.target.value)} />
                        </div>
                    </div>
                ) : (
                    <div className="text-sm text-muted-foreground">
                        {rangeType === "all" ? "Показаны данные за все время" : "Даты считаются автоматически для выбранного периода"}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function Analytics() {
    const [searchParams] = useSearchParams();
    const { rangeType, startYmd, endYmd, startDate, endDate, actions } = useAnalyticsUI();

    const {
        getPeople,
        people,
        prevPeople,
        getPrevPeople,
        getClosedCredits,
        totalPayments: payments,
        closedCredits,
        prevClosedCredits,
        prevTotalPayments,
    } = usePeople();

    useEffect(() => {
        const isAll = rangeType === "all";
        const isCustom = rangeType === "custom";

        getPeople({
            dateRange: isAll ? undefined : (rangeType as any),
            customStart: isCustom ? startDate : undefined,
            customEnd: isCustom ? endDate : undefined,
        });

        getClosedCredits({
            dateRange: isAll ? undefined : (rangeType as any),
            customStart: isCustom ? startDate : undefined,
            customEnd: isCustom ? endDate : undefined,
        });

        if (!isAll) {
            const unit = rangeType === "year" || rangeType === "month" || rangeType === "week" ? rangeType : "year";
            const prevStart = dayjs(startDate).subtract(1, unit).toDate();
            const prevEnd = dayjs(endDate).subtract(1, unit).toDate();

            getPrevPeople({
                dateRange: rangeType as any,
                customStart: isCustom ? prevStart : undefined,
                customEnd: isCustom ? prevEnd : undefined,
            });

            getClosedCredits({
                dateRange: rangeType as any,
                customStart: isCustom ? prevStart : undefined,
                customEnd: isCustom ? prevEnd : undefined,
                isPrev: true,
            });
        }
    }, [getPeople, getPrevPeople, getClosedCredits, rangeType, startDate, endDate]);

    const analyticsData = useAnalyticsData(
        people,
        prevPeople,
        searchParams,
        payments,
        prevTotalPayments,
        closedCredits,
        prevClosedCredits
    );

    const {
        totalTurnover,
        contractCount,
        percentContractsChange,
        netIncome,
        percentIncomeChange,
        productPreferences,
        percentTurnoverChange,
        monthsArray,
        monthlyContracts,
        totalClientsArray,
        totalPayments,
        percentPaymentsChange,
        closedContractsCount,
        percentClosedContractsChange,
        totalCostPrice,
        percentCostPriceChange,
        closedCreditsNetProfit,
        percentClosedNetProfitChange,
    } = analyticsData;

    const periodLabel = rangeType === "all" ? "все время" : getPeriod(rangeType as any);

    const turnoverProgress = useMemo(() => {
        const v = Number(totalTurnover || 0);
        const base = Math.max(1, v);
        return clampPercent((v / base) * 100);
    }, [totalTurnover]);

    const incomeProgress = useMemo(() => {
        const v = Number(netIncome || 0);
        const base = Math.max(1, Number(totalTurnover || 0));
        return clampPercent((v / base) * 100);
    }, [netIncome, totalTurnover]);

    const costShare = useMemo(() => {
        const cost = Number(totalCostPrice || 0);
        const turn = Math.max(1, Number(totalTurnover || 0));
        return clampPercent((cost / turn) * 100);
    }, [totalCostPrice, totalTurnover]);

    const closedShare = useMemo(() => {
        const closed = Number(closedContractsCount || 0);
        const all = Math.max(1, Number(contractCount || 0));
        return clampPercent((closed / all) * 100);
    }, [closedContractsCount, contractCount]);

    const statusDistribution = useMemo(() => {
        const counts: Record<string, number> = {};

        for (const p of people || []) {
            const s = (p as any).status ?? "unknown";
            counts[s] = (counts[s] || 0) + 1;
        }

        const rows = STATUS_META
            .map((s) => ({
                key: s.key,
                name: s.label,
                value: counts[s.key] || 0,
            }))
            .filter((x) => x.value > 0);

        if (!rows.length) {
            const unknown = counts.unknown || 0;
            return unknown ? [{ key: "unknown", name: "Другое", value: unknown }] : [];
        }

        return rows;
    }, [people]);

    const isAll = rangeType === "all";

    const allTimeClientsRows = useMemo(() => {
        if (!isAll) return [];

        const map = new Map<string, { monthly: number }>();

        for (const p of people || []) {
            const d = (p as any).createdAt ? dayjs((p as any).createdAt) : null;
            if (!d || !d.isValid()) continue;

            const key = d.format("YYYY-MM");
            const item = map.get(key) || { monthly: 0 };
            item.monthly += 1;
            map.set(key, item);
        }

        const entries = Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));

        let run = 0;
        return entries.map(([ym, v]) => {
            run += v.monthly;
            return {
                month: dayjs(ym + "-01").format("MMM YY"),
                monthly: v.monthly,
                total: run,
            };
        });
    }, [isAll, people]);

    const clientsRows = useMemo(() => {
        if (isAll) return allTimeClientsRows;

        const len = Math.max(monthsArray?.length || 0, monthlyContracts?.length || 0, totalClientsArray?.length || 0);
        return Array.from({ length: len }).map((_, i) => ({
            month: monthsArray?.[i] ?? `M${i + 1}`,
            monthly: monthlyContracts?.[i] ?? 0,
            total: totalClientsArray?.[i] ?? 0,
        }));
    }, [isAll, allTimeClientsRows, monthsArray, monthlyContracts, totalClientsArray]);

    const allTimeSalesRows = useMemo(() => {
        if (!isAll) return [];

        const map = new Map<string, { turnover: number; income: number; contracts: number }>();

        for (const p of people || []) {
            const d = (p as any).createdAt ? dayjs((p as any).createdAt) : null;
            if (!d || !d.isValid()) continue;

            const key = d.format("YYYY-MM");
            const item = map.get(key) || { turnover: 0, income: 0, contracts: 0 };

            item.contracts += 1;

            // IMPORTANT: подставь свои реальные поля сумм, если отличаются
            const turnoverVal = Number((p as any).creditDetail?.totalSum ?? (p as any).amount ?? 0);
            const incomeVal = Number((p as any).profit ?? (p as any).income ?? 0);

            item.turnover += Number.isFinite(turnoverVal) ? turnoverVal : 0;
            item.income += Number.isFinite(incomeVal) ? incomeVal : 0;

            map.set(key, item);
        }

        return Array.from(map.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([ym, v]) => ({
                month: dayjs(ym + "-01").format("MMM YY"),
                turnover: v.turnover,
                income: v.income,
                contracts: v.contracts,
            }));
    }, [isAll, people]);

    const salesRows = useMemo(() => {
        if (isAll) return allTimeSalesRows;

        const t = analyticsData.salesTrend?.turnover || [];
        const inc = analyticsData.salesTrend?.income || [];
        const contracts = monthlyContracts || [];
        const len = Math.max(monthsArray?.length || 0, t.length, inc.length, contracts.length);

        return Array.from({ length: len }).map((_, i) => ({
            month: monthsArray?.[i] ?? `M${i + 1}`,
            turnover: t[i] ?? 0,
            income: inc[i] ?? 0,
            contracts: contracts[i] ?? 0,
        }));
    }, [isAll, allTimeSalesRows, analyticsData.salesTrend?.turnover, analyticsData.salesTrend?.income, monthsArray, monthlyContracts]);

    const overdueCount = useMemo(() => {
        const row = statusDistribution.find((x) => x.key === "overdue");
        return row?.value ?? 0;
    }, [statusDistribution]);

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Аналитика</h1>
                    <p className="text-muted-foreground">
                        {rangeType === "custom" ? (
                            <span>
                Период:{" "}
                                <span className="font-medium text-foreground">
                  {dayjs(startDate).format("DD.MM.YYYY")} - {dayjs(endDate).format("DD.MM.YYYY")}
                </span>
              </span>
                        ) : (
                            <span>
                Период: <span className="font-medium text-foreground">{periodLabel}</span>
              </span>
                        )}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="text-chart-1">
                        <TrendingUp className="mr-1 h-3 w-3" />
                        {isAll ? "Оборот" : `${Number(percentTurnoverChange || 0) >= 0 ? "+" : ""}${Number(percentTurnoverChange || 0).toFixed(1)}%`}
                    </Badge>

                    <Badge variant="outline" className="text-chart-2">
                        <TrendingUp className="mr-1 h-3 w-3" />
                        {isAll ? "Прибыль" : `${Number(percentIncomeChange || 0) >= 0 ? "+" : ""}${Number(percentIncomeChange || 0).toFixed(1)}%`}
                    </Badge>
                </div>
            </div>

            <RangeFilter
                rangeType={rangeType}
                startYmd={startYmd}
                endYmd={endYmd}
                onRangeChange={actions.setRangeType}
                onDatesChange={(s, e) => actions.setCustomDates(dayjs(s).toDate(), dayjs(e).toDate())}
            />

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title="Оборот"
                    value={totalTurnover ? `${formatNumber(totalTurnover)}` : "0"}
                    icon={DollarSign}
                    className="border border-border/70 bg-gradient-to-br from-primary/10 via-background to-background backdrop-blur-sm"
                    description={
                        <span className="inline-flex items-center gap-2">
              {isAll ? <Badge variant="secondary">суммарно</Badge> : pctBadge(Number(percentTurnoverChange || 0))}
                            <span className="text-xs text-muted-foreground">{periodLabel}</span>
            </span>
                    }
                />

                <StatCard
                    title="Выдано кредитов"
                    value={contractCount ? `${formatNumber(contractCount)} чел.` : "0"}
                    icon={Users}
                    className="border border-border/70 bg-gradient-to-br from-chart-2/10 via-background to-background backdrop-blur-sm"
                    description={
                        <span className="inline-flex items-center gap-2">
              {isAll ? <Badge variant="secondary">всего</Badge> : pctBadge(Number(percentContractsChange || 0))}
                            <span className="text-xs text-muted-foreground">{periodLabel}</span>
            </span>
                    }
                />

                <StatCard
                    title="Чистая прибыль"
                    value={netIncome ? `${formatNumber(netIncome)}` : "0"}
                    icon={CheckCircle2}
                    className="border border-border/70 bg-gradient-to-br from-emerald-500/10 via-background to-background backdrop-blur-sm"
                    description={
                        <span className="inline-flex items-center gap-2">
              {isAll ? <Badge variant="secondary">суммарно</Badge> : pctBadge(Number(percentIncomeChange || 0))}
                            <span className="text-xs text-muted-foreground">{periodLabel}</span>
            </span>
                    }
                />

                <StatCard
                    title="Выплаты"
                    value={totalPayments ? `${formatNumber(totalPayments)}` : "0"}
                    icon={Wallet}
                    className="border border-border/70 bg-gradient-to-br from-indigo-500/10 via-background to-background backdrop-blur-sm"
                    description={
                        <span className="inline-flex items-center gap-2">
              {isAll ? <Badge variant="secondary">суммарно</Badge> : pctBadge(Number(percentPaymentsChange || 0))}
                            <span className="text-xs text-muted-foreground">{periodLabel}</span>
            </span>
                    }
                />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                <Card className="overflow-hidden">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-sm text-muted-foreground">Оборот</p>
                                <p className="text-2xl font-bold">{totalTurnover ? formatNumber(totalTurnover) : "0"}</p>
                            </div>
                            {isAll ? <Badge variant="secondary">суммарно</Badge> : pctBadge(Number(percentTurnoverChange || 0))}
                        </div>
                        <Progress value={turnoverProgress} className="mt-3 h-2" />
                        <p className="mt-2 text-xs text-muted-foreground">Визуальный прогресс</p>
                    </CardContent>
                </Card>

                <Card className="overflow-hidden">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-sm text-muted-foreground">Прибыль</p>
                                <p className="text-2xl font-bold">{netIncome ? formatNumber(netIncome) : "0"}</p>
                            </div>
                            {isAll ? <Badge variant="secondary">суммарно</Badge> : pctBadge(Number(percentIncomeChange || 0))}
                        </div>
                        <Progress value={incomeProgress} className="mt-3 h-2" />
                        <p className="mt-2 text-xs text-muted-foreground">Относительно оборота</p>
                    </CardContent>
                </Card>

                <Card className="overflow-hidden">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-sm text-muted-foreground">Закрыто кредитов</p>
                                <p className="text-2xl font-bold">{closedContractsCount ? formatNumber(closedContractsCount) : "0"}</p>
                            </div>
                            {isAll ? <Badge variant="secondary">всего</Badge> : pctBadge(Number(percentClosedContractsChange || 0))}
                        </div>
                        <Progress value={closedShare} className="mt-3 h-2" />
                        <p className="mt-2 text-xs text-muted-foreground">Доля закрытых</p>
                    </CardContent>
                </Card>
            </div>

            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList className="grid w-full max-w-2xl grid-cols-3">
                    <TabsTrigger value="overview">Обзор</TabsTrigger>
                    <TabsTrigger value="clients">Клиенты</TabsTrigger>
                    <TabsTrigger value="portfolio">Портфель</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    <div className="grid gap-6 lg:grid-cols-2">
                        <Card className="lg:col-span-2 overflow-hidden">
                            <CardHeader>
                                <CardTitle>Динамика</CardTitle>
                                <CardDescription>Оборот, прибыль и договоры</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[360px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={salesRows}>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                            <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-xs fill-muted-foreground" />
                                            <YAxis
                                                yAxisId="left"
                                                tickLine={false}
                                                axisLine={false}
                                                className="text-xs fill-muted-foreground"
                                                tickFormatter={moneyTick}
                                            />
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                tickLine={false}
                                                axisLine={false}
                                                className="text-xs fill-muted-foreground"
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: "hsl(var(--popover))",
                                                    border: "1px solid hsl(var(--border))",
                                                    borderRadius: "var(--radius)",
                                                    color: "hsl(var(--popover-foreground))",
                                                }}
                                            />
                                            <Legend />
                                            <Area
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="turnover"
                                                name="Оборот"
                                                stroke="hsl(var(--chart-1))"
                                                fill="hsl(var(--chart-1))"
                                                fillOpacity={0.18}
                                                strokeWidth={2}
                                            />
                                            <Line
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="income"
                                                name="Прибыль"
                                                stroke="hsl(var(--chart-2))"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                            <Bar yAxisId="right" dataKey="contracts" name="Договоры" fill="hsl(var(--chart-3))" radius={[6, 6, 0, 0]} />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="overflow-hidden">
                            <CardHeader>
                                <CardTitle>Статусы</CardTitle>
                                <CardDescription>Распределение клиентов по статусам</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[320px]">
                                    {statusDistribution.length ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={statusDistribution}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={105}
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    labelLine={false}
                                                    label={({ name, value }) => `${name}: ${value}`}
                                                >
                                                    {statusDistribution.map((entry) => (
                                                        <Cell key={entry.key} fill={STATUS_COLORS[entry.key] ?? STATUS_COLORS.unknown} />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: "hsl(var(--popover))",
                                                        border: "1px solid hsl(var(--border))",
                                                        borderRadius: "var(--radius)",
                                                        color: "hsl(var(--popover-foreground))",
                                                    }}
                                                />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
                                            Нет данных по статусам за выбранный период
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="overflow-hidden">
                            <CardHeader>
                                <CardTitle>Сводка</CardTitle>
                                <CardDescription>Выплаты, закрытия, прибыль закрытых</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex items-center justify-between rounded-lg border p-3">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Banknote className="h-4 w-4 text-muted-foreground" />
                                        Выплаты
                                    </div>
                                    <div className="text-right">
                                        <div className="font-semibold">{totalPayments ? formatNumber(totalPayments) : "0"}</div>
                                        <div className="mt-1">
                                            {isAll ? <Badge variant="secondary">суммарно</Badge> : pctBadge(Number(percentPaymentsChange || 0))}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between rounded-lg border p-3">
                                    <div className="flex items-center gap-2 text-sm">
                                        <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                                        Закрыто кредитов
                                    </div>
                                    <div className="text-right">
                                        <div className="font-semibold">{closedContractsCount ? formatNumber(closedContractsCount) : "0"}</div>
                                        <div className="mt-1">
                                            {isAll ? <Badge variant="secondary">всего</Badge> : pctBadge(Number(percentClosedContractsChange || 0))}
                                        </div>
                                    </div>
                                </div>

                                <Separator />

                                <div className="flex items-center justify-between rounded-lg bg-muted/30 p-3">
                                    <div className="text-sm text-muted-foreground">Чистая прибыль закрытых</div>
                                    <div className="text-right">
                                        <div className="font-semibold">{closedCreditsNetProfit ? formatNumber(closedCreditsNetProfit) : "0"}</div>
                                        <div className="mt-1">
                                            {isAll ? <Badge variant="secondary">суммарно</Badge> : pctBadge(Number(percentClosedNetProfitChange || 0))}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="lg:col-span-2 overflow-hidden">
                            <CardHeader>
                                <CardTitle>Предпочтения продукта</CardTitle>
                                <CardDescription>Топ позиций за период</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ProductPreferencesCard productPreferences={productPreferences || []} />
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="clients" className="space-y-4">
                    <Card className="overflow-hidden">
                        <CardHeader>
                            <CardTitle>Клиенты</CardTitle>
                            <CardDescription>Новые договоры и накопление</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[380px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={clientsRows}>
                                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                        <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-xs fill-muted-foreground" />
                                        <YAxis tickLine={false} axisLine={false} className="text-xs fill-muted-foreground" />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: "hsl(var(--popover))",
                                                border: "1px solid hsl(var(--border))",
                                                borderRadius: "var(--radius)",
                                                color: "hsl(var(--popover-foreground))",
                                            }}
                                        />
                                        <Legend />
                                        <Bar dataKey="monthly" name="Новые" fill="hsl(var(--chart-2))" radius={[6, 6, 0, 0]} />
                                        <Bar dataKey="total" name="Всего" fill="hsl(var(--chart-4))" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="portfolio" className="space-y-4">
                    <div className="grid gap-6 lg:grid-cols-2">
                        <Card className="overflow-hidden">
                            <CardHeader>
                                <CardTitle>{`Вложено за ${periodLabel}`}</CardTitle>
                                <CardDescription>Себестоимость по выданным</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex items-center justify-between rounded-lg border p-3">
                                    <div className="text-sm text-muted-foreground">Сумма</div>
                                    <div className="text-right">
                                        <div className="text-xl font-semibold">{totalCostPrice ? formatNumber(totalCostPrice) : "0"}</div>
                                        <div className="mt-1">
                                            {isAll ? <Badge variant="secondary">суммарно</Badge> : pctBadge(Number(percentCostPriceChange || 0))}
                                        </div>
                                    </div>
                                </div>

                                <Progress value={costShare} className="h-2" />
                                <p className="text-xs text-muted-foreground">Доля вложений от оборота</p>
                            </CardContent>
                        </Card>

                        <Card className="overflow-hidden">
                            <CardHeader>
                                <CardTitle>Итоги портфеля</CardTitle>
                                <CardDescription>Ключевые метрики</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex items-center justify-between rounded-lg border p-3">
                                    <div className="text-sm text-muted-foreground">Выдано кредитов</div>
                                    <div className="font-semibold">{contractCount ? formatNumber(contractCount) : "0"}</div>
                                </div>

                                <div className="flex items-center justify-between rounded-lg border p-3">
                                    <div className="text-sm text-muted-foreground">Закрыто кредитов</div>
                                    <div className="font-semibold">{closedContractsCount ? formatNumber(closedContractsCount) : "0"}</div>
                                </div>

                                <div className="flex items-center justify-between rounded-lg border p-3">
                                    <div className="text-sm text-muted-foreground">Просрочено</div>
                                    <div className="font-semibold inline-flex items-center gap-2">
                                        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                                        {String(overdueCount)}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
